// Client-side scenario model: what the user has placed on the map before
// issuing. Param schemas come from GET /v1/catalog; this module never
// hard-codes a disaster's params, only how to preview its reach.

const KM_PER_DEG = 111.32;

let counter = 0;
export function newId(type) {
  counter += 1;
  return `${type.slice(0, 3)}-${Date.now().toString(36).slice(-4)}${counter.toString(36)}`;
}

export function defaultsFor(spec) {
  return Object.fromEntries(spec.params.map((p) => [p.key, p.default]));
}

export function newScenario(spec, lat, lon, id = newId(spec.type)) {
  return { id, type: spec.type, lat: round4(lat), lon: round4(wrapLon(lon)), params: defaultsFor(spec) };
}

export const round4 = (v) => Math.round(v * 1e4) / 1e4;
export const wrapLon = (lon) => ((((lon + 180) % 360) + 360) % 360) - 180;

export function clampParam(field, value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return field.default;
  return Math.min(field.max, Math.max(field.min, v));
}

export function validate(scenarios, catalog, horizon, parameters) {
  const errors = [];
  if (Array.isArray(parameters) && !parameters.length) errors.push("Pick at least one parameter.");
  if (!scenarios.length) errors.push("Place at least one disaster on the map.");
  if (scenarios.length > (catalog?.defaults?.max_scenarios ?? 8)) errors.push("Too many disasters (max 8).");
  for (const s of scenarios) {
    if ((s.params.start_hour ?? 0) > horizon) errors.push(`${s.id}: starts after the ${horizon} h horizon.`);
  }
  return errors;
}

/** The POST /v1/issues body. start_hour travels as a top-level field;
 * `parameters` only when the picker is not "all" (see parameters.js). */
export function toIssueRequest(scenarios, { horizon, resolution, name, seed, parameters, alertMinutes } = {}) {
  return {
    ...(name ? { name } : {}),
    ...(parameters ? { parameters } : {}),
    horizon_hours: horizon,
    ...(alertMinutes != null ? { alert_minutes: alertMinutes } : {}),
    ...(resolution ? { resolution_deg: resolution } : {}),
    ...(seed != null ? { seed } : {}),
    scenarios: scenarios.map((s) => {
      const { start_hour = 0, ...params } = s.params;
      return { id: s.id, type: s.type, lat: s.lat, lon: s.lon, start_hour: Math.round(start_hour), params: { ...params, start_hour } };
    }),
  };
}

export function movePoint(lat, lon, eastKm, northKm) {
  const nlat = lat + northKm / KM_PER_DEG;
  const c = Math.max(Math.cos(((lat + nlat) / 2) * Math.PI / 180), 0.05);
  return [nlat, lon + eastKm / (KM_PER_DEG * c)];
}

/** Approximate reach (km) for the ring drawn before issuing. Mirrors the API's footprints. */
export function previewRadiusKm(s) {
  const p = s.params;
  switch (s.type) {
    case "meteor": return 150 * Math.cbrt(p.energy_mt);
    case "hurricane": return 6 * p.rmax_km;
    case "volcano": return 25 + 8 * p.vei;
    case "wildfire": return Math.max(Math.sqrt(p.size_ha / 100 / Math.PI) * 4, 10);
    default: return 50;
  }
}

/** Projected hurricane centre positions for hours 0..horizon (lat, lon). */
export function hurricaneTrack(s, horizon) {
  const out = [];
  const h = (s.params.heading_deg * Math.PI) / 180;
  const span = Math.max(horizon - (s.params.start_hour || 0), 0);
  for (let t = 0; t <= span; t += 1) {
    const d = s.params.speed_kmh * t;
    out.push(movePoint(s.lat, s.lon, d * Math.sin(h), d * Math.cos(h)));
  }
  return out;
}

export function reducer(state, action) {
  switch (action.type) {
    case "add":
      return [...state, action.scenario];
    case "move":
      return state.map((s) => (s.id === action.id ? { ...s, lat: round4(action.lat), lon: round4(wrapLon(action.lon)) } : s));
    case "param":
      return state.map((s) => (s.id === action.id ? { ...s, params: { ...s.params, [action.key]: action.value } } : s));
    case "remove":
      return state.filter((s) => s.id !== action.id);
    case "replace":
      return action.scenarios;
    case "clear":
      return [];
    default:
      return state;
  }
}
